import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { StopOutlined } from '@ant-design/icons';
import { StageTaskType } from '@/store/Lifecycle/constants';
import { LifecycleStoreTask } from '@/store/Lifecycle/types';
import { useStores } from '@/hooks/use-stores';
import EPButton from '@/components/EPButton/EPButton';
import { StyledStage } from './LifecycleStage.styles';
import { DueDateProgress } from './components/DueDateProgress';
import { useStage } from '../Provider';
import AddBlockReasonModal from './components/AddBlockModal';
import UserIdsSection from './components/UserIdsSection';
import { groupTasks } from '../../taskComponents/constants';
import { TasksRow } from './components/TasksRow';
import { MoveToStageButton } from './components/MoveToStageButton';
import { sortTransitionsWithSkippedStages } from '../utils';

const LifecycleStage = observer(() => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { lifecycleStore, stationStore } = useStores();
  const { isCurrent, transitions, isBlocked, isFinished, tasks, hasEditPermission, expectedDuration } = useStage();
  const { setIsBlockedReasonModalOpen } = lifecycleStore;

  const infoCardTasks = useMemo(
    () =>
      tasks
        .filter(task => task.type === StageTaskType.UserId)
        .sort((a, b) => a.order - b.order) as LifecycleStoreTask<StageTaskType.UserId>[],
    [tasks],
  );
  const tasksWithoutInfoCards = useMemo(
    () => groupTasks(tasks.filter(task => task.type !== StageTaskType.UserId)),
    [tasks],
  );

  const sortedTransitions = useMemo(
    () => sortTransitionsWithSkippedStages(transitions, stationStore.stages),
    [transitions, stationStore.stages],
  );

  const handleBlockStage = () => {
    setIsBlockedReasonModalOpen(true);
  };

  const handleUnblockStage = async () => {
    message.loading({
      content: t('Unblocking stage...'),
      key: 'unblock-stage-modal',
      duration: 15,
    });
    const isUnblocked = await lifecycleStore.unblockStage();
    if (isUnblocked) {
      message.success({
        content: t('Stage unblocked successfully'),
        key: 'unblock-stage-modal',
      });
    } else {
      message.error({
        content: t('Failed to unblock stage'),
        key: 'unblock-stage-modal',
      });
    }
  };

  return (
    <StyledStage form={form} $isBlocked={isBlocked}>
      <div className="tasks-container">
        {!isFinished && !!expectedDuration && <DueDateProgress />}
        {infoCardTasks.length > 0 && <UserIdsSection tasks={infoCardTasks} />}
        {tasksWithoutInfoCards.map(({ name, tasks }) => (
          <TasksRow key={name} name={name} tasks={tasks} />
        ))}
      </div>
      {isCurrent && (
        <div className="stage-cta-container">
          {sortedTransitions.length > 0 &&
            sortedTransitions.map(transition => <MoveToStageButton key={transition.id} transition={transition} />)}
          <EPButton
            type="default"
            onClick={isBlocked ? handleUnblockStage : handleBlockStage}
            className={`${hasEditPermission && 'block-button'}`}
            disabled={!hasEditPermission}
          >
            <StopOutlined />
            {t(isBlocked ? 'Unblock stage' : 'Block stage')}
          </EPButton>
          {isCurrent && <AddBlockReasonModal />}
        </div>
      )}
    </StyledStage>
  );
});

export default LifecycleStage;
